import { Input } from "./ui/input";
import { UploadTracker } from "./UploadTracker";
import { useEffect, useState, useCallback } from "react";
import { KeetaInvoiceView } from "./KeetaInvoiceView";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Loader2, Trash2, Receipt, Table2, Users, Printer, Maximize2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Money, SaudiRiyal } from "./SaudiRiyal";
import { api } from "../lib/api";
import { InvoiceEligibility } from "./InvoiceEligibility";
import { RiderIdImportDialog } from "./RiderIdImportDialog";

const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const fmtMonth = (m) => {
  if (!m) return "بدون شهر";
  const [y, mm] = String(m).split("-");
  const idx = Number(mm) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS_AR[idx]} ${y}` : m;
};
const numFmt = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  if (typeof v !== "number") return String(v);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

const RIDER_KEYS = ["rider_id", "معرف المندوب"];
const SheetTable = ({ sheet, nameMap = {}, maxH = "max-h-[420px]" }) => {
  if (!sheet || sheet.rows.length === 0) return <div className="py-10 text-center text-sm text-slate-400">لا توجد بيانات في هذه الورقة</div>;
  const ridIdx = sheet.headers.findIndex((h) => RIDER_KEYS.some((k) => String(h).includes(k)));
  const resolveName = (v) => { let s = String(v).trim(); if (s.endsWith(".0")) s = s.slice(0, -2); return nameMap[s] || ""; };
  return (
    <div className={`overflow-auto ${maxH}`}>
      <table className="w-full text-xs min-w-[560px]">
        <thead className="sticky top-0 bg-slate-50 text-slate-500">
          <tr>
            {ridIdx >= 0 && <th className="px-2.5 py-2 text-right whitespace-nowrap font-semibold">المندوب</th>}
            {sheet.headers.map((h, i) => (
              <th key={i} className="px-2.5 py-2 text-right whitespace-nowrap font-semibold">{String(h).split(" - ")[0]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sheet.rows.map((row, ri) => (
            <tr key={ri} data-testid={`monthly-sheet-row-${ri}`} className="border-t border-slate-100 hover:bg-slate-50/60">
              {ridIdx >= 0 && (
                <td className="px-2.5 py-1.5 text-right whitespace-nowrap font-semibold text-slate-800">
                  {resolveName(row[ridIdx]) || <span className="text-amber-500 font-normal">غير مرتبط</span>}
                </td>
              )}
              {row.map((cell, ci) => (
                <td key={ci} className="px-2.5 py-1.5 text-right whitespace-nowrap text-slate-700" dir={typeof cell === "number" ? "ltr" : undefined}>
                  {numFmt(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// A styled revenue-invoice summary built from the "Co LVL" sheet.
const TotalInvoice = ({ summary, month }) => {
  const s = summary || {};
  const Row = ({ label, value, strong, tone, riyal = true }) => (
    <div className={`flex items-center justify-between px-4 py-2.5 ${strong ? "bg-emerald-50" : ""}`}>
      <span className={`text-sm ${strong ? "font-bold text-emerald-800" : "text-slate-500"}`}>{label}</span>
      <span className={`font-mono-nums ${strong ? "font-extrabold text-lg" : "font-semibold"} ${tone || "text-slate-800"}`}>
        {riyal ? <Money value={value} /> : value}
      </span>
    </div>
  );
  return (
    <div data-testid="monthly-total-invoice" className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="flex items-center justify-between gap-3 bg-gradient-to-l from-emerald-600 to-teal-600 px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          <div>
            <div className="font-heading font-bold text-base">الفاتورة الإجمالية</div>
            <div className="text-[11px] opacity-90">{s.contract_name || "—"} · {fmtMonth(month)}</div>
          </div>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${s.vat_status === "Yes" ? "bg-white/25" : "bg-white/15"}`}>
          {s.vat_status === "Yes" ? "خاضع للضريبة" : "غير خاضع"}{s.vat_no ? ` · ${s.vat_no}` : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-px bg-slate-100 border-b border-slate-100">
        <div className="bg-white px-4 py-2.5 text-center">
          <div className="text-[11px] text-slate-400">الطلبات المكتملة</div>
          <div className="font-heading font-bold text-slate-800">{numFmt(s.completed_orders)}</div>
        </div>
        <div className="bg-white px-4 py-2.5 text-center">
          <div className="text-[11px] text-slate-400">الدفعة الأساسية</div>
          <div className="font-heading font-bold text-slate-800"><Money value={s.basic_payment} /></div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        <Row label="المبلغ قبل الضريبة" value={s.amount_excl_vat} />
        <Row label="ضريبة القيمة المضافة (15%)" value={s.vat} tone="text-amber-600" />
        <Row label="المبلغ شامل الضريبة" value={s.amount_incl_vat} />
        <Row label="إجمالي الخصومات" value={s.total_deduction} tone="text-red-600" />
        <Row label="صافي المستحق FTR" value={s.net_ftr} strong tone="text-emerald-700" />
      </div>
    </div>
  );
};

const money2 = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const printRiderStatement = (rider, month) => {
  const w = window.open("", "_blank");
  if (!w) { toast.error("اسمح بالنوافذ المنبثقة للطباعة"); return; }
  w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>سند تسوية مندوب</title>
    <style>
      *{box-sizing:border-box} body{font-family:SaudiRiyal,'Tajawal','IBM Plex Sans Arabic',sans-serif;color:#0f172a;padding:40px;max-width:640px;margin:auto}
      h1{font-size:22px;border-bottom:3px solid #059669;padding-bottom:12px;margin:0 0 6px}
      .sub{color:#64748b;font-size:13px;margin-bottom:18px}
      table{width:100%;border-collapse:collapse;font-size:14px}
      th,td{border:1px solid #e2e8f0;padding:11px 12px;text-align:right} th{background:#f1f5f9;width:180px;font-weight:700;color:#334155}
      .net td{background:#f0fdf4;font-weight:800;color:#065f46;font-size:16px}
      .foot{margin-top:26px;color:#94a3b8;font-size:12px;text-align:center;border-top:1px dashed #cbd5e1;padding-top:12px}
    </style>
    <script>window.onload=function(){setTimeout(function(){window.print()},350)}<\/script>
    </head><body>
    <h1>سند تسوية مندوب</h1><div class="sub">شهر ${fmtMonth(month)}</div>
    <table>
      <tr><th>اسم المندوب</th><td>${rider.name || "—"}</td></tr>
      <tr><th>معرّف المندوب</th><td dir="ltr">${rider.rider_id}</td></tr>
      <tr><th>الطلبات المكتملة</th><td>${rider.completed_orders}</td></tr>
      <tr><th>إجمالي الأرباح (بدلات + أساسي)</th><td>${money2(rider.earnings)} ⃁</td></tr>
      <tr><th>إجمالي الخصومات والغرامات</th><td>${money2(rider.deductions)} ⃁</td></tr>
      <tr><th>صافي الفاتورة</th><td>${money2(rider.net)} ⃁</td></tr>
      ${rider.allocated_expense ? `<tr><th>نصيب المصروفات (بنزين/صيانة…)</th><td>${money2(rider.allocated_expense)} ⃁</td></tr>` : ""}
      <tr class="net"><th>الربحية الصافية</th><td>${money2(rider.profit != null ? rider.profit : rider.net)} ⃁</td></tr>
    </table>
    <div class="foot">تاريخ الطباعة: ${new Date().toLocaleDateString("ar-EG")}</div>
    </body></html>`);
  w.document.close();
  w.focus();
};

const RiderBreakdown = ({ invoice, canEdit }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState("orders");
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => { setData(null); setOpen(false); }, [invoice?.id]);

  const fetchRiders = async (b) => {
    setLoading(true);
    try { const d = await api.getInvoiceRiders(invoice.id, b); setData(d); setOpen(true); }
    catch { toast.error("تعذّر تحميل المندوبين"); }
    finally { setLoading(false); }
  };
  const toggle = () => { if (data) { setOpen((o) => !o); return; } fetchRiders(basis); };
  const changeBasis = (b) => { setBasis(b); if (data) fetchRiders(b); };

  const BASIS_LABEL = { orders: "حسب عدد الطلبات", equal: "بالتساوي", net: "حسب الصافي", liters: "حسب اللترات المستهلكة" };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button data-testid="riders-breakdown-toggle" onClick={toggle} className="flex w-full items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700"><Users className="h-4 w-4 text-teal-500" /> ربحية كل مندوب (بالأسماء)</span>
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
          <span className="text-xs text-slate-500">{data ? `${data.matched} مطابق · ${data.unmatched} غير مطابق` : "عرض ›"}</span>
        )}
      </button>
      {open && data && (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap border-t border-slate-100 bg-slate-50/60 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">توزيع المصروفات:</span>
              <select data-testid="riders-basis-select" value={basis} onChange={(e) => changeBasis(e.target.value)}
                className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs">
                <option value="orders">حسب عدد الطلبات</option>
                <option value="equal">بالتساوي</option>
                <option value="net">حسب الصافي</option>
                <option value="liters">حسب اللترات المستهلكة</option>
              </select>
              {data.total_expenses > 0 && <span className="text-[11px] text-red-500">إجمالي موزّع: {money2(data.total_expenses)} ⃁</span>}
              {basis === "liters" && data.total_liters === 0 && <span className="text-[11px] text-amber-600">لا توجد لترات مربوطة بالمندوبين — تم التوزيع بالتساوي</span>}
            </div>
            {canEdit && data.unmatched > 0 && (
              <button data-testid="riders-import-ids" onClick={() => setImportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-indigo-700">
                <Upload className="h-3.5 w-3.5" /> استيراد معرفات المندوبين
              </button>
            )}
          </div>
          <div className="overflow-auto max-h-[460px]">
            <table className="w-full text-xs min-w-[680px]">
              <thead className="sticky top-0 bg-slate-50 text-slate-500"><tr>
                <th className="px-2.5 py-2 text-right">المندوب</th>
                <th className="px-2.5 py-2">الطلبات</th>
                <th className="px-2.5 py-2">صافي الفاتورة</th>
                <th className="px-2.5 py-2">نصيب المصروفات</th>
                <th className="px-2.5 py-2">الربحية</th>
                <th className="px-2.5 py-2">سند</th>
              </tr></thead>
              <tbody>
                {data.riders.map((r, i) => (
                  <tr key={i} data-testid={`rider-net-row-${r.rider_id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-2.5 py-1.5 text-right">
                      {r.name ? <span className="font-semibold text-slate-800">{r.name}</span> : <span className="text-amber-600" dir="ltr">{r.rider_id}</span>}
                      {r.name && <span className="text-slate-400 text-[10px] block" dir="ltr">{r.rider_id}</span>}
                    </td>
                    <td className="px-2.5 py-1.5 text-center" dir="ltr">{r.completed_orders}</td>
                    <td className="px-2.5 py-1.5 text-center text-slate-700" dir="ltr">{money2(r.net)}</td>
                    <td className="px-2.5 py-1.5 text-center text-red-500" dir="ltr">{r.allocated_expense ? money2(r.allocated_expense) : "—"}</td>
                    <td className={`px-2.5 py-1.5 text-center font-bold ${(r.profit ?? r.net) >= 0 ? "text-teal-700" : "text-orange-600"}`} dir="ltr">{money2(r.profit != null ? r.profit : r.net)}</td>
                    <td className="px-2.5 py-1.5 text-center">
                      <button data-testid={`rider-print-${r.rider_id}`} onClick={() => printRiderStatement(r, invoice.month)} className="p-1 rounded text-slate-500 hover:bg-slate-100"><Printer className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-bold text-slate-700"><tr>
                <td className="px-2.5 py-2 text-right">الإجمالي ({data.riders.length} مندوب)</td>
                <td></td>
                <td className="px-2.5 py-2 text-center" dir="ltr">{money2(data.total_net)}</td>
                <td className="px-2.5 py-2 text-center text-red-500" dir="ltr">{money2(data.total_expenses)}</td>
                <td className="px-2.5 py-2 text-center text-teal-700" dir="ltr">{money2(data.total_profit)}</td>
                <td></td>
              </tr></tfoot>
            </table>
          </div>
        </>
      )}
      <RiderIdImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => fetchRiders(basis)} />
    </div>
  );
};

export const MonthlyInvoiceCard = ({ canEdit, platform = "هنجرستيشن" }) => {
  const isHunger = platform === "هنجرستيشن";
  const [list, setList] = useState([]);
  const [selId, setSelId] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [month, setMonth] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingInv, setLoadingInv] = useState(false);
  const [nameMap, setNameMap] = useState({});
  const [fsOpen, setFsOpen] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const l = await api.listMonthlyInvoices(platform);
      setList(l);
      setSelId((cur) => cur || (l[0]?.id || ""));
    } catch { /* ignore */ }
  }, [platform]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { api.getRiderNameMap().then(setNameMap).catch(() => {}); }, []);

  useEffect(() => {
    if (!selId) { setInvoice(null); return; }
    setLoadingInv(true);
    api.getMonthlyInvoice(selId).then((d) => { setInvoice(d); setSheetIdx(0); }).catch(() => {}).finally(() => setLoadingInv(false));
  }, [selId]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy(true);
    try {
      const d = await api.uploadMonthlyInvoice(f, month, platform);
      toast.success(`تم رفع الفاتورة وقراءة ${d.sheets?.length || 0} أوراق`);
      if (d.autolink) toast.info(`ربط تلقائي: ${d.autolink.linked} مندوب (${d.autolink.by_name} بالاسم) · بدون ربط ${d.autolink.unlinked}`);
      if (d.invalid_notified) toast.warning(`${d.invalid_notified} مندوب غير صالح — أُرسل تنبيه بالأسماء والأسباب`);
      await loadList();
      setSelId(d.id); setInvoice(d); setSheetIdx(0);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر قراءة الملف"); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!selId) return;
    try { await api.deleteMonthlyInvoice(selId); toast.success("تم الحذف"); setSelId(""); setInvoice(null); loadList(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  const sheet = invoice?.sheets?.[sheetIdx];

  return (
    <div className="rounded-2xl border border-indigo-100 bg-indigo-50/30 p-4 space-y-4" data-testid="monthly-invoice-card">
      <UploadTracker params={{ section: "invoices", platform, year: (month || "").slice(0, 4) || String(new Date().getFullYear()) }} rev={busy} title={`فواتير ${platform}`} testid="ut-invoices" canEdit={canEdit} onDeleted={() => { setSelId(""); loadList(); }} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600"><FileSpreadsheet className="h-5 w-5" /></div>
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900" data-testid="monthly-invoice-title">فاتورة {platform} الشهرية</h3>
            <p className="text-[11px] text-slate-500">ارفع ملف Excel الشهري لفاتورة {platform} (عدة أوراق) واقرأه مباشرة</p>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="month" data-testid="monthly-invoice-month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-9 rounded-lg border border-indigo-200 bg-white px-2 text-sm text-indigo-800" title="اختر الشهر" />
            <input type="file" accept=".xlsx,.xls" id="monthly-inv-file" className="hidden" data-testid="monthly-invoice-input" onChange={onUpload} />
            <Button disabled={busy} onClick={() => document.getElementById("monthly-inv-file").click()}
              className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid="monthly-invoice-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع فاتورة {platform}
            </Button>
          </div>
        )}
      </div>

      {list.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500">الشهر:</span>
          <select data-testid="monthly-invoice-select" value={selId} onChange={(e) => setSelId(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
            {list.map((it) => <option key={it.id} value={it.id}>{fmtMonth(it.month)}{it.filename ? ` — ${it.filename}` : ""}</option>)}
          </select>
          {canEdit && selId && (
            <button data-testid="monthly-invoice-delete" onClick={del} className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-red-500 hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5" /> حذف
            </button>
          )}
        </div>
      )}

      {loadingInv ? (
        <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : !invoice ? (
        <div className="py-8 text-center text-sm text-slate-400" data-testid="monthly-invoice-empty">
          لم تُرفع أي فاتورة بعد. ارفع ملف Excel الشهري لعرض الفاتورة الإجمالية وقراءة الأوراق.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 items-start">
          <TotalInvoice summary={invoice.summary} month={invoice.month} />

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700"><Table2 className="h-4 w-4 text-slate-400" /> تبويبات الملف</span>
              <div className="flex items-center gap-1.5">
                <select data-testid="monthly-invoice-sheet-select" value={sheetIdx} onChange={(e) => setSheetIdx(Number(e.target.value))}
                  className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs">
                  {(invoice.sheets || []).map((sh, i) => <option key={i} value={i}>{`${sh.name} (${sh.rows.length})`}</option>)}
                </select>
                <button data-testid="monthly-invoice-maximize" onClick={() => setFsOpen(true)} title="تكبير الشاشة"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50">
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <SheetTable sheet={sheet} nameMap={nameMap} />
          </div>

          <Dialog open={fsOpen} onOpenChange={setFsOpen}>
            <DialogContent dir="rtl" className="max-w-[96vw] w-[96vw] max-h-[92vh]">
              <DialogHeader className="text-start">
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <Table2 className="h-5 w-5 text-slate-400" />
                  {sheet?.name} — {fmtMonth(invoice.month)}
                  <select data-testid="fs-sheet-select" value={sheetIdx} onChange={(e) => setSheetIdx(Number(e.target.value))}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-body">
                    {(invoice.sheets || []).map((sh, i) => <option key={i} value={i}>{`${sh.name} (${sh.rows.length})`}</option>)}
                  </select>
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <SheetTable sheet={sheet} nameMap={nameMap} maxH="max-h-[78vh]" />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {invoice && invoice.summary?.source_format === "keeta" && <KeetaInvoiceView invoiceId={invoice.id} canEdit={canEdit} summary={invoice.summary} />}
      {invoice && isHunger && <InvoiceEligibility invoiceId={invoice.id} />}
      {invoice && isHunger && <RiderBreakdown invoice={invoice} canEdit={canEdit} />}
    </div>
  );
};
